import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronRight, ChevronDown, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle, RefreshCw, Wrench, ListChecks, LayoutGrid, FileSpreadsheet, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { JobDetailModal } from "../components/JobDetailModal";
import { StatusBadge, PriorityBadge, DisciplineBadge } from "../components/Badges";
import { LineActionPicker, StatusTimeline, LineNotes, CalPeriodTag } from "../components/job-detail/JobLinesTable";
import { EditLineModal } from "../components/EditLineModal";
import { RemoveLineModal } from "../components/RemoveLineModal";
import { useLineActionRunner } from "../components/job-detail/useLineActionRunner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { fmtNum } from "../lib/format";

const STATUS_TABS = [
  ["", "All in-lab"],
  ["received", "Received"],
  ["assigned", "Assigned"],
  ["in_progress", "In progress"],
  ["awaiting_spares", "Awaiting spares"],
  ["awaiting_customer_approval", "Awaiting customer"],
];

// Statuses available in the bulk-action dropdown. Excludes the messy commercial /
// dispatch terminals which need dedicated modals (invoice, dispatch details, etc.).
// SPT-PTE-2026-0043: "ready_for_admin" removed — calibration-complete lines now
// go straight to "ready_for_delivery" (In Dispatch), no manual admin stage.
const BULK_TARGET_STATUSES = [
  "received", "assigned", "in_progress",
  "calibrated", "adjusted", "repaired",
  "parts_required", "awaiting_spares",
  "awaiting_customer_approval",
  "ready_for_delivery",
];

function BulkBar({ selectedIds, clearSelection, onDone, technicians }) {
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [techId, setTechId] = useState("");
  const apply = async () => {
    if (!target) return toast.error("Pick a target status first.");
    if (!window.confirm(`Move ${selectedIds.length} item(s) to "${target}"?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/job-items/bulk-status", { item_ids: selectedIds, status: target, notes: notes || null });
      const skipped = (r.data.skipped || []).length;
      if (skipped) toast.warning(`${r.data.count} updated · ${skipped} skipped (see audit log)`);
      else toast.success(`${r.data.count} item(s) moved to "${target}"`);
      clearSelection();
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const reassign = async () => {
    if (!techId) return toast.error("Pick a technician first.");
    const techName = technicians.find((t) => t.id === techId)?.name || "selected technician";
    if (!window.confirm(`Reassign ${selectedIds.length} item(s) to ${techName}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/job-items/bulk-technician", { item_ids: selectedIds, technician_id: techId });
      const skipped = (r.data.skipped || []).length;
      if (skipped) toast.warning(`${r.data.count} reassigned · ${skipped} skipped (see audit log)`);
      else toast.success(`${r.data.count} item(s) reassigned to ${techName}`);
      clearSelection();
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="active-lab-bulk-status">
        <option value="">— Pick target status —</option>
        {BULK_TARGET_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>
      <input className="input" placeholder="Optional note (audited)" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="active-lab-bulk-note"
              style={{ minWidth: 200, flex: 1 }} />
      <button className="btn gold sm" onClick={apply} disabled={busy || !target}
              data-testid="active-lab-bulk-apply">
        {busy ? <><RefreshCw size={13} className="spin" /> Applying…</> : <><ListChecks size={13} /> Apply to {selectedIds.length}</>}
      </button>
      <select className="input" value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="active-lab-bulk-technician">
        <option value="">— Reassign technician —</option>
        {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <button className="btn ghost sm" onClick={reassign} disabled={busy || !techId}
              data-testid="active-lab-bulk-reassign-apply">
        {busy ? <><RefreshCw size={13} className="spin" /> Applying…</> : "Reassign"}
      </button>
    </>
  );
}

function DrillSortTh({ label, col, sort, onSort, jobId }) {
  const active = sort.by === col;
  return (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => onSort(col)}
      data-testid={`al-drill-sort-${jobId}-${col}`}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} style={{ opacity: 0.4 }} />}
      </span>
    </th>
  );
}

// Expanded drill-down for one job: its equipment lines, sortable client-side.
// Each line also gets an inline "Select action… → Go" picker so a
// technician/admin can action a specific item without opening the full job,
// plus a chevron to expand a per-line status timeline (instead of an
// "Open job" round-trip just to find a serial number again).
function DrillTable({ jobId, items, onRunAction, busy, canEdit, isTechnician, isManager, onEditLine, onRemoveLine, activeStatus }) {
  const [sort, setSort] = useState({ by: "status", dir: "asc" });
  const [openTimeline, setOpenTimeline] = useState(null);
  // Same "solo focus" fix as My Worklist: keep the last-actioned line pinned
  // at the top (regardless of whether its new status still matches the
  // active tab) so a chain of actions (Repair -> Adjust -> Calibrate ->
  // Send to Admin) on one item doesn't make it jump back into the rest of
  // the list between each step. Clears on Send to Admin.
  const [pinnedId, setPinnedId] = useState(null);
  const handleRunAction = (action, item) => {
    setPinnedId(action === "send_to_admin" ? null : item.id);
    onRunAction(action, item);
  };
  const onSort = (col) => setSort((s) => (s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "asc" }));
  const { sorted, matchCount } = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (it) => (sort.by === "cr" ? (it.certificate_number || it.reserved_certificate_number) : it[sort.by]) || "";
    const cmp = (a, b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }) * dir;
    const pinned = pinnedId ? items.find((it) => it.id === pinnedId) : null;
    const rest0 = pinned ? items.filter((it) => it.id !== pinnedId) : items;
    if (!activeStatus) {
      const s = [...rest0].sort(cmp);
      return { sorted: pinned ? [pinned, ...s] : s, matchCount: s.length + (pinned ? 1 : 0) };
    }
    // Surface the lines matching the currently-selected status tab at the
    // top of the drill-down — the rest of the job's lines stay visible
    // underneath (nothing is hidden), just grouped out of the way since
    // they aren't relevant to the tab you're working from.
    const match = rest0.filter((it) => it.status === activeStatus).sort(cmp);
    const rest = rest0.filter((it) => it.status !== activeStatus).sort(cmp);
    const sorted = pinned ? [pinned, ...match, ...rest] : [...match, ...rest];
    return { sorted, matchCount: (pinned ? 1 : 0) + match.length };
  }, [items, sort, activeStatus, pinnedId]);
  const thp = { sort, onSort, jobId };
  return (
    <table className="dt" style={{ background: "var(--card)" }} data-testid={`active-lab-drill-${jobId}`}>
      <thead>
        <tr>
          <th style={{ width: 28 }}></th>
          <DrillSortTh label="Equipment" col="description" {...thp} />
          <DrillSortTh label="Serial #" col="serial_number" {...thp} />
          <DrillSortTh label="Part #" col="part_number" {...thp} />
          <DrillSortTh label="Issue #" col="issue_number" {...thp} />
          <DrillSortTh label="Status" col="status" {...thp} />
          <th>Cal Period</th>
          <DrillSortTh label="Discipline" col="discipline" {...thp} />
          <DrillSortTh label="Technician" col="technician_name" {...thp} />
          <DrillSortTh label="Job Ref / CR #" col="cr" {...thp} />
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((it, idx) => {
          const isOpen = openTimeline === it.id;
          const showDivider = activeStatus && idx === matchCount && matchCount > 0 && matchCount < sorted.length;
          return (
          <React.Fragment key={it.id}>
            {showDivider && (
              <tr data-testid={`active-lab-drill-divider-${jobId}`}>
                <td colSpan={11} style={{ padding: "6px 14px", background: "var(--card-2)", borderTop: "1px dashed var(--line)", borderBottom: "1px dashed var(--line)" }}>
                  <span className="t-mut" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".03em" }}>
                    Other lines on this job (not "{STATUS_TABS.find(([k]) => k === activeStatus)?.[1] || activeStatus}")
                  </span>
                </td>
              </tr>
            )}
            <tr style={it.id === pinnedId ? { background: "#eef6ff" } : undefined}>
              <td>
                <button className="icon-btn" style={{ width: 22, height: 22 }}
                  onClick={() => setOpenTimeline(isOpen ? null : it.id)}
                  title="Status timeline" data-testid={`active-lab-timeline-toggle-${it.id}`}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              </td>
              <td><span className="t-strong">{it.description || "—"}</span><LineNotes remarks={it.remarks} notes={it.notes} testId={`active-lab-notes-${it.id}`} /></td>
              <td className="mono" style={{ fontSize: 11.5 }}>{it.serial_number || "—"}</td>
              <td className="mono" style={{ fontSize: 11.5 }}>{it.part_number || "—"}</td>
              <td className="mono" style={{ fontSize: 11.5 }}>{it.issue_number || "—"}</td>
              <td>
                <StatusBadge status={it.status} /><PriorityBadge priority={it.priority} />
                {it.id === pinnedId && (
                  <span className="t-mut" style={{ marginLeft: 6, fontSize: 10.5, fontStyle: "italic" }} data-testid={`active-lab-pinned-${it.id}`}>
                    · working on this
                  </span>
                )}
              </td>
              <td><CalPeriodTag days={it.calibration_period_days} testId={`active-lab-calperiod-${it.id}`} /></td>
              <td>{it.discipline ? <DisciplineBadge name={it.discipline} /> : "—"}</td>
              <td className="t-mut" style={{ fontSize: 12 }}>{it.technician_name || "Unassigned"}</td>
              <td className="mono t-mut" style={{ fontSize: 11.5 }}>{it.certificate_number || it.reserved_certificate_number || "—"}</td>
              <td>
                <div className="line-action-cell">
                  {!isTechnician && canEdit && (
                    <button className="icon-btn" title="Edit line" onClick={() => onEditLine(it)}
                      data-testid={`active-lab-edit-${it.id}`}><Pencil size={12} /></button>
                  )}
                  {isManager && (
                    <button className="icon-btn" title="Remove line" onClick={() => onRemoveLine(it)}
                      data-testid={`active-lab-remove-${it.id}`}><Trash2 size={12} /></button>
                  )}
                  <LineActionPicker item={it} onRun={handleRunAction} busy={busy} />
                </div>
              </td>
            </tr>
            {isOpen && (
              <tr data-testid={`active-lab-timeline-${it.id}`}>
                <td></td>
                <td colSpan={10} style={{ background: "var(--card-2)", padding: "10px 14px" }}>
                  <StatusTimeline history={it.status_history || []} />
                </td>
              </tr>
            )}
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function ByJobView({ statusTab, disciplineFilter, refreshSignal, openJob, onActionApplied, technicians }) {
  const { user, can } = useAuth();
  const isTechnician = user?.role === "technician";
  const isManager = user?.role === "manager" || user?.role === "admin";
  const canEdit = can("equipment", "edit");
  const [editingLine, setEditingLine] = useState(null);
  const [removingLine, setRemovingLine] = useState(null);
  // Custom server-side aggregated view: one row per Job (DN) + drill-down to items.
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(50);
  const [sort, setSort] = useState({ by: "updated_at", dir: "desc" });
  const [expanded, setExpanded] = useState({}); // { job_id: true }
  const [drillData, setDrillData] = useState({}); // { job_id: [items] }
  const [drillBusy, setDrillBusy] = useState({});
  // SPT-PTE-2026-0024: job-level multi-select for the "Move to Awaiting
  // Spares" bulk action + per-job/bulk technician reassignment.
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [bulkTechId, setBulkTechId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const SORTABLE_COLS = new Set(["delivery_note_number", "customer_name", "date_received"]);
  const toggleSort = (key) => {
    if (!SORTABLE_COLS.has(key)) return;
    setSort((s) => (s.by === key ? { by: key, dir: s.dir === "asc" ? "desc" : "asc" } : { by: key, dir: "asc" }));
  };

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setSkip(0); }, [debounced, statusTab, refreshSignal, limit, sort.by, sort.dir]);
  useEffect(() => { setSelectedJobs(new Set()); setBulkTechId(""); }, [statusTab, debounced, skip]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/active-lab/jobs", { params: {
      search: debounced || undefined, limit, skip, sort_by: sort.by, sort_dir: sort.dir,
      status: statusTab || undefined, discipline_q: disciplineFilter || undefined,
    } })
      .then((r) => { setRows(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [debounced, skip, limit, sort.by, sort.dir, statusTab, disciplineFilter]);
  useEffect(() => { load(); }, [load, refreshSignal]);

  // A change signalled from OUTSIDE this view (e.g. a line removed/edited via
  // "Open job"'s Job Detail modal) only bumps `refreshSignal` — the effect
  // above re-fetches the top-level row list, but any ALREADY-EXPANDED
  // drill-down table was left showing its stale cached `drillData` (removed/
  // merged duplicate lines kept appearing there even though the job itself
  // was already correct). Refresh every currently-expanded job's items too.
  useEffect(() => {
    Object.keys(expanded).forEach((jobId) => {
      if (!expanded[jobId]) return;
      setDrillBusy((p) => ({ ...p, [jobId]: true }));
      api.get("/job-items", { params: { job_id: jobId, active_only: true, limit: 200, sort_by: "status" } })
        .then((r) => setDrillData((p) => ({ ...p, [jobId]: r.data.items || [] })))
        .catch(() => {})
        .finally(() => setDrillBusy((p) => ({ ...p, [jobId]: false })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const toggleExpand = async (jobId) => {
    const willExpand = !expanded[jobId];
    setExpanded((p) => ({ ...p, [jobId]: willExpand }));
    if (willExpand && !drillData[jobId]) {
      setDrillBusy((p) => ({ ...p, [jobId]: true }));
      try {
        const r = await api.get("/job-items", { params: { job_id: jobId, active_only: true, limit: 200, sort_by: "status" } });
        setDrillData((p) => ({ ...p, [jobId]: r.data.items || [] }));
      } catch (e) { toast.error(apiErr(e)); } finally { setDrillBusy((p) => ({ ...p, [jobId]: false })); }
    }
  };

  const toggleJobSelect = (jobId) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };
  const toggleSelectAllJobs = () => {
    setSelectedJobs((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  };

  // SPT-PTE-2026-0024: reassign the technician on one or many DNs (the
  // per-row quick dropdown next to "Open job", or the multi-select bulk
  // bar) — scoped to whatever lines match the currently active status tab
  // (e.g. only the "assigned" lines), so a mixed-status DN never has its
  // already-completed/dispatched lines silently touched.
  const reassignTechnician = async (jobIds, technicianId) => {
    const techName = technicianId ? (technicians.find((t) => t.id === technicianId)?.name || "selected technician") : "Unassigned";
    const scopeLabel = statusTab ? ` "${STATUS_TABS.find(([k]) => k === statusTab)?.[1] || statusTab}"` : "";
    if (!window.confirm(`Reassign ${jobIds.length} job(s)'${scopeLabel} item(s) to ${techName}?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/job-items/bulk-technician", {
        job_ids: jobIds, source_status: statusTab || undefined, technician_id: technicianId || null,
      });
      const skipped = (r.data.skipped || []).length;
      if (r.data.count === 0 && !skipped) toast.warning("No matching items to reassign on the selected job(s).");
      else if (skipped) toast.warning(`${r.data.count} item(s) reassigned to ${techName} · ${skipped} skipped`);
      else toast.success(`${r.data.count} item(s) reassigned to ${techName}`);
      setSelectedJobs(new Set());
      setBulkTechId("");
      reloadAfterAction();
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };

  // Bulk-move every line matching the active status tab, on the selected
  // DNs, across to Awaiting Spares — requires a specific tab (not "All
  // in-lab") so the source scope is unambiguous.
  const moveToAwaitingSpares = async (jobIds) => {
    if (!statusTab) return toast.error('Pick a specific status tab (e.g. "Assigned") first, then select jobs to move.');
    if (!window.confirm(`Move ${jobIds.length} job(s)' "${STATUS_TABS.find(([k]) => k === statusTab)?.[1]}" item(s) to Awaiting Spares?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/active-lab/jobs/bulk-status", {
        job_ids: jobIds, source_status: statusTab, target_status: "awaiting_spares",
      });
      const skipped = (r.data.skipped || []).length;
      if (skipped) toast.warning(`${r.data.count} moved to Awaiting Spares · ${skipped} skipped`);
      else toast.success(`${r.data.count} item(s) moved to Awaiting Spares`);
      setSelectedJobs(new Set());
      reloadAfterAction();
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };

  // After an inline line action, refresh the top-level rows AND every
  // currently-expanded job's drill-down items (so the status/action column
  // updates in place without the tech losing their scroll position), plus
  // bubble up to the parent so the status-count tabs stay in sync too.
  const reloadAfterAction = useCallback(() => {
    load();
    onActionApplied && onActionApplied();
    Object.keys(expanded).forEach((jobId) => {
      if (!expanded[jobId]) return;
      api.get("/job-items", { params: { job_id: jobId, active_only: true, limit: 200, sort_by: "status" } })
        .then((r) => setDrillData((p) => ({ ...p, [jobId]: r.data.items || [] })))
        .catch(() => {});
    });
  }, [load, expanded, onActionApplied]);
  const { runLineAction, busy: actionBusy, modals: actionModals } = useLineActionRunner(reloadAfterAction);

  // Filtering by status/discipline is now done server-side (see load() above)
  // so `rows` already reflects the active filters — no client-side re-filter
  // needed (that used to silently truncate results to whatever happened to
  // be on the current unfiltered page, showing "0 results" for real matches
  // sitting on a later page).

  return (
    <div data-testid="active-lab-by-job">
      <div className="ftabs" style={{ alignItems: "center", marginBottom: 12 }}>
        {selectedJobs.size > 0 ? (
          <>
            <span className="t-strong" data-testid="active-lab-jobs-selected-count">{selectedJobs.size} job(s) selected</span>
            <select className="input" style={{ maxWidth: 190 }} value={bulkTechId}
              onChange={(e) => setBulkTechId(e.target.value)} data-testid="active-lab-bulk-jobs-technician">
              <option value="">— Reassign technician —</option>
              <option value="__unassign__">Unassigned</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button className="btn ghost sm" disabled={bulkBusy || !bulkTechId}
              onClick={() => reassignTechnician([...selectedJobs], bulkTechId === "__unassign__" ? "" : bulkTechId)}
              data-testid="active-lab-bulk-jobs-reassign-btn">
              {bulkBusy ? <><RefreshCw size={13} className="spin" /> Applying…</> : "Reassign"}
            </button>
            <button className="btn gold sm" disabled={bulkBusy || !statusTab}
              title={!statusTab ? 'Pick a specific status tab (e.g. "Assigned") to enable this' : undefined}
              onClick={() => moveToAwaitingSpares([...selectedJobs])}
              data-testid="active-lab-bulk-jobs-awaiting-spares-btn">
              {bulkBusy ? <><RefreshCw size={13} className="spin" /> Applying…</> : "Move to Awaiting Spares"}
            </button>
            <button className="btn ghost sm" onClick={() => setSelectedJobs(new Set())} data-testid="active-lab-bulk-jobs-clear">Clear</button>
          </>
        ) : <span style={{ flex: 1 }} />}
        <div className="search" style={{ maxWidth: 320 }}>
          <input placeholder="Search DN, customer…" value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="active-lab-by-job-search" />
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="dt">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input type="checkbox" data-testid="active-lab-select-all-jobs"
                  checked={rows.length > 0 && selectedJobs.size === rows.length}
                  onChange={toggleSelectAllJobs} />
              </th>
              <th style={{ width: 32 }}></th>
              <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("delivery_note_number")} data-testid="active-lab-by-job-th-dn">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Delivery Note
                  {sort.by === "delivery_note_number" && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                </span>
              </th>
              <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("customer_name")} data-testid="active-lab-by-job-th-customer">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Customer
                  {sort.by === "customer_name" && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                </span>
              </th>
              <th style={{ width: 80, textAlign: "center" }}>Items</th>
              <th>Status spread</th>
              <th>Technicians</th>
              <th>Discipline</th>
              <th style={{ width: 120, cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("date_received")} data-testid="active-lab-by-job-th-received">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Received
                  {sort.by === "date_received" && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                </span>
              </th>
              <th style={{ width: 220 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="t-mut" style={{ padding: 30, textAlign: "center" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="t-mut" style={{ padding: 30, textAlign: "center" }}>
                No jobs match the current filter.
              </td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(r.id)}
                     data-testid={`active-lab-job-row-${r.id}`}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedJobs.has(r.id)} onChange={() => toggleJobSelect(r.id)}
                      data-testid={`active-lab-job-select-${r.id}`} />
                  </td>
                  <td>
                    {expanded[r.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </td>
                  <td>
                    <span className="t-strong mono">{r.delivery_note_number || "—"}</span>
                    {r.urgent && <AlertTriangle size={12} color="#c0303a" style={{ marginLeft: 6, verticalAlign: -1 }} />}
                  </td>
                  <td>
                    <span className="t-strong">{r.customer_name || "—"}</span>
                    {r.customer_number && <span className="t-mut mono" style={{ marginLeft: 6, fontSize: 11 }}>{r.customer_number}</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span className="badge b-gold"><span className="bd" />{r.item_count}</span>
                  </td>
                  <td>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {r.statuses.slice(0, 4).map((s) => <StatusBadge key={s} status={s} />)}
                      {r.statuses.length > 4 && <span className="t-mut" style={{ fontSize: 11 }}>+{r.statuses.length - 4}</span>}
                    </div>
                  </td>
                  <td className="t-mut" style={{ fontSize: 12 }}>
                    {r.technicians.length === 0 ? <i>Unassigned</i>
                      : r.technicians.length === 1 ? r.technicians[0]
                      : `${r.technicians.length} techs`}
                  </td>
                  <td>
                    {r.disciplines.slice(0, 2).map((d) => <DisciplineBadge key={d} name={d} />)}
                    {r.disciplines.length > 2 && <span className="t-mut" style={{ fontSize: 11, marginLeft: 4 }}>+{r.disciplines.length - 2}</span>}
                  </td>
                  <td className="mono t-mut" style={{ fontSize: 12 }}>{r.date_received || "—"}</td>
                  <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <select className="input" style={{ padding: "3px 6px", height: "auto", fontSize: 11.5, maxWidth: 130 }}
                        value="" onChange={(e) => { if (e.target.value) reassignTechnician([r.id], e.target.value === "__unassign__" ? "" : e.target.value); }}
                        title="Reassign the technician on this job's items" data-testid={`active-lab-job-reassign-${r.id}`}>
                        <option value="">Reassign tech…</option>
                        <option value="__unassign__">Unassigned</option>
                        {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <button className="btn ghost sm" onClick={() => openJob(r.id)}
                        data-testid={`active-lab-job-open-${r.id}`}>Open job</button>
                    </div>
                  </td>
                </tr>
                {expanded[r.id] && (
                  <tr>
                    <td colSpan={10} style={{ padding: 0, background: "var(--card-2)" }}>
                      <div style={{ padding: "10px 14px 14px 38px" }}>
                        {drillBusy[r.id] && <div className="t-mut" style={{ fontSize: 12, padding: 12 }}>Loading items…</div>}
                        {drillData[r.id] && (
                          <DrillTable jobId={r.id} items={drillData[r.id]} onRunAction={runLineAction} busy={actionBusy}
                            canEdit={canEdit} isTechnician={isTechnician} isManager={isManager}
                            onEditLine={setEditingLine} onRemoveLine={setRemovingLine} activeStatus={statusTab} />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ftabs" style={{ marginTop: 10 }}>
        {(total > limit || skip > 0) ? (
          <>
            <span className="t-mut" style={{ fontSize: 12 }}>{skip + 1}-{Math.min(skip + limit, total)} of {total}</span>
            <span style={{ flex: 1 }} />
            <button className="btn ghost sm" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0}>← Prev</button>
            <button className="btn ghost sm" onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total}>Next →</button>
          </>
        ) : (
          <>
            <span className="t-mut" style={{ fontSize: 12 }}>{total} job{total !== 1 ? "s" : ""}</span>
            <span style={{ flex: 1 }} />
          </>
        )}
        <label className="t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginLeft: 8 }}>
          Rows per page
          <select className="input" style={{ padding: "4px 8px", height: "auto" }} value={limit}
            onChange={(e) => setLimit(Number(e.target.value))} data-testid="active-lab-by-job-page-size">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {actionModals}
      {editingLine && (
        <EditLineModal item={editingLine} onClose={() => setEditingLine(null)}
          onSaved={() => { setEditingLine(null); reloadAfterAction(); }} />
      )}
      {removingLine && (
        <RemoveLineModal item={removingLine} onClose={() => setRemovingLine(null)}
          onRemoved={() => { setRemovingLine(null); reloadAfterAction(); }} />
      )}
    </div>
  );
}

export default function ActiveLab() {
  const { user, can } = useAuth();
  const isTechnician = user?.role === "technician";
  const canEditTable = !isTechnician && can("equipment", "edit");
  const [selJob, setSelJob] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  useEffect(() => {
    // SPT — "Reassign technician" must only offer actual technicians, not
    // every active employee (admin/parts/technical_manager/dispatch, etc.) —
    // filter server-side by role, matching every other technician picker in
    // the app (Receiving.js, EditLineModal.js, AddLineModal.js, MoveEquipmentToJobModal.js).
    api.get("/employees", { params: { role: "technician" } })
      .then((r) => setTechnicians((r.data || []).filter((e) => e.active !== false))).catch(() => {});
  }, []);
  useEffect(() => {
    // Supports deep-linking straight to a job's modal, e.g. from the
    // Receiving screen's "this DN already exists" warning link.
    const id = new URLSearchParams(window.location.search).get("openJob");
    if (id) setSelJob(id);
  }, []);
  // SPT — technicians land on "Assigned" by default (their actual queue to
  // work through); managers/admins keep the broader "All in-lab" default.
  const [statusTab, setStatusTab] = useState(() => (isTechnician ? "assigned" : ""));
  const [disciplineFilter, setDisciplineFilter] = useState(() => localStorage.getItem("active-lab.disciplineFilter") || "");
  const [disciplines, setDisciplines] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ total: 0, counts: {} });
  const [refresh, setRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  // Toggle persisted per-user
  const [view, setView] = useState(() => localStorage.getItem("active-lab.view.v2") || "jobs");
  useEffect(() => { localStorage.setItem("active-lab.view.v2", view); }, [view]);
  useEffect(() => { localStorage.setItem("active-lab.disciplineFilter", disciplineFilter); }, [disciplineFilter]);

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines((r.data || []).filter((d) => d.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/active-lab/status-counts", { params: disciplineFilter ? { discipline: disciplineFilter } : {} })
      .then((r) => setStatusCounts(r.data || { total: 0, counts: {} }))
      .catch(() => {});
  }, [disciplineFilter, refresh]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/active-lab/export.xlsx", { responseType: "blob", timeout: 120000 });
      let fname = `Active_Lab_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) fname = m[1];
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Active Lab report downloaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const columns = [
    { key: "delivery_note_number", label: "Delivery note", sortable: true, colSearch: true, mono: true, render: (v) => <span className="t-strong mono">{v || "—"}</span> },
    { key: "description", label: "Equipment", sortable: true, colSearch: true, render: (v, row) => (
      <span>
        <span className="t-strong">{v || "—"}</span>
        <LineNotes remarks={row.remarks} notes={row.notes} testId={`active-lab-eq-notes-${row.id}`} />
      </span>
    ) },
    { key: "serial_number", label: "Serial #", sortable: true, colSearch: true, render: (v) => <span className="mono">{v || "—"}</span> },
    { key: "issue_number", label: "Issue #", sortable: true, colSearch: true, render: (v) => <span className="mono">{v || "—"}</span> },
    { key: "customer_name", label: "Customer", sortable: true, colSearch: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "discipline", label: "Discipline", sortable: true, render: (v) => <DisciplineBadge name={v} /> },
    { key: "calibration_period_days", label: "Cal Period", sortable: true, render: (v, row) => <CalPeriodTag days={v} testId={`active-lab-flat-calperiod-${row.id}`} /> },
    {
      key: "technician_id", label: "Technician", sortable: true, editable: true, type: "select",
      options: technicians.map((t) => ({ value: t.id, label: t.name })),
      render: (v, row) => <span className="t-mut">{row.technician_name || "Unassigned"}</span>,
    },
    {
      key: "status", label: "Status", sortable: true, editable: true, type: "select",
      editEndpoint: (row) => `/job-items/${row.id}/status`,
      options: BULK_TARGET_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") })),
      render: (v, row) => (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <StatusBadge status={v} /> <PriorityBadge priority={row.priority} />
        </span>
      ),
    },
  ];

  const openRow = (row) => { if (row.job_id) setSelJob(row.job_id); };
  const openJob = (jobId) => { if (jobId) setSelJob(jobId); };
  const params = {
    ...(statusTab ? { status: statusTab } : { active_only: true }),
    ...(disciplineFilter ? { discipline_q: disciplineFilter } : {}),
  };
  const bump = () => setRefresh((x) => x + 1);

  // The status-tab + view-toggle bar — shared across both views.
  const headerBar = (
    <>
      <div className="ftabs" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(([k, l]) => (
          <button key={k || "all"} className={`ftab ${statusTab === k ? "active" : ""}`}
                  data-testid={`active-lab-tab-${k || "all"}`} onClick={() => setStatusTab(k)}>
            {l} <span className="c" data-testid={`active-lab-tab-count-${k || "all"}`}>{fmtNum(k ? (statusCounts.counts[k] || 0) : statusCounts.total)}</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <select className="input" style={{ maxWidth: 190 }} value={disciplineFilter}
          onChange={(e) => setDisciplineFilter(e.target.value)} data-testid="active-lab-discipline-filter">
          <option value="">All disciplines</option>
          {disciplines.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
        </select>
        <button className="btn ghost sm" onClick={exportXlsx} disabled={exporting}
                data-testid="active-lab-export-xlsx-btn" title="Export every active-lab item to Excel, grouped by DN">
          {exporting ? <><RefreshCw size={13} className="spin" /> Exporting…</> : <><FileSpreadsheet size={13} /> Export to Excel</>}
        </button>
        <div style={{ display: "inline-flex", gap: 4, padding: 3, background: "var(--card-2)", borderRadius: 8 }}>
          <button className={`ftab ${view === "equipment" ? "active" : ""}`}
                  data-testid="active-lab-view-equipment"
                  onClick={() => setView("equipment")}
                  title="One row per equipment item (multi-select enabled)">
            <Wrench size={13} /> By equipment
          </button>
          <button className={`ftab ${view === "jobs" ? "active" : ""}`}
                  data-testid="active-lab-view-jobs"
                  onClick={() => setView("jobs")}
                  title="Group by Delivery Note — one row per Job with drill-down">
            <LayoutGrid size={13} /> By Job (D/N)
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <PageHead crumb="Operations" title="Active Lab"
        sub="Every job-line currently in the lab. Switch to 'By Job' for a one-row-per-Delivery-Note summary that drills down to its items, or stay on 'By equipment' to multi-select and apply bulk status changes." />
      {headerBar}
      {view === "equipment" ? (
        <DataTable endpoint="/job-items" params={params} columns={columns} rowKey="id" testid="active-lab"
          defaultSort={{ by: "created_at", dir: "desc" }} searchPlaceholder="Search DN, equipment, customer…" initialLimit={100}
          onRowClick={openRow} refreshSignal={refresh}
          canEdit={canEditTable} editEndpoint={(row) => `/job-items/${row.id}`} batchEdit
          selectable
          bulkToolbar={(ids, clear) => <BulkBar selectedIds={ids} clearSelection={clear} onDone={bump} technicians={technicians} />}
        />
      ) : (
        <ByJobView statusTab={statusTab} disciplineFilter={disciplineFilter} refreshSignal={refresh} openJob={openJob} onActionApplied={bump} technicians={technicians} />
      )}
      {selJob && <JobDetailModal jobId={selJob} onClose={() => setSelJob(null)} onChanged={bump} />}
    </>
  );
}
